<template>
  <section
    id="rvCarousel"
    ref="RvCarousel"
    class="rv-carousel no-touch relative z-0"
    aria-roledescription="carousel"
    :aria-label="arialabel"
    @mouseover="handleRotation(false)"
    @mouseleave="handleRotation(rotating ? true : false)"
  >
    <div class="rv-carousel__inner relative">
      <ul>
        <li>
          <button
            class="rv-carousel__btn rv-carousel__btn--previous"
            :class="[ btnClasses, 'left-0' ]"
            @click="handlePrevNext('prev')"
            @focus="handleRotation(false)"
            @blur="handleRotation(rotating ? true : false)"
            aria-label="Previous Slide"
            aria-controls="rvCarousel-items"
          >
            <SvgIcon
              :class="btnIconClasses"
              icon="chevron-left"
            />
          </button>
        </li>
        <li>
          <button
            class="rv-carousel__btn rv-carousel__btn--next"
            :class="[ btnClasses, 'right-0' ]"
            @click="handlePrevNext('next')"
            @focus="handleRotation(false)"
            @blur="handleRotation(rotating ? true : false)"
            aria-controls="rvCarousel-items"
            aria-label="Next Slide"
          >
            <SvgIcon
              :class="btnIconClasses"
              icon="chevron-right"
            />
          </button>
        </li>
      </ul>

      <div
        id="rvCarousel-items"
        ref="RvCarouselItems"
        class="rv-carousel__items relative w-full"
        :style="{ paddingBottom: '75%' }"
        :aria-live="rotate ? 'off' : 'polite'"
      >
        <TransitionGroup
          name="fade"
          mode="in-out"
        >
          <div
            v-for="(image, i1) in gallery"
            :key="`image-${image.id}`"
            v-show="currentIndex === i1"
            class="rv-carousel__item a-cover flex justify-center items-center"
            role="group"
            aria-roledescription="slide"
          >
            <img
              class="rv-carousel__image relative block"
              :class="image.sizes['large-width'] >= image.sizes['large-height'] ? 'max-w-full h-auto' : 'w-auto max-h-full'"
              :src="lazyLoad.includes(i1) && image.sizes.large"
              :width="image.sizes['large-width']"
              :height="image.sizes['large-height']"
              :alt="`Gallery item ${i1 + 1} of ${gallery.length}, ${image.alt}`"
              :title="htmlDecode(image.title)"
            >
            <div
              class="rv-carousel__info absolute left-0 bottom-0 w-full px-12 py-3 flex-c bg-accent-1 text-center"
              aria-hidden="true"
            >
              <h3 class="rv-carousel__title">
                <span
                  class="rv-carousel__count"
                  v-html="`${i1 + 1}/${gallery.length}`"
                />
                <span
                  v-if="image.caption"
                  class="mx-1"
                  v-html="' &mdash; '"
                />
                <span
                  v-if="image.caption"
                  class="rv-carousel__caption"
                  v-html="image.caption"
                />
              </h3>
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>
  </section>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'

export default {
  name: 'RvCarousel',

  components: {
    SvgIcon
  },

  props: {
    arialabel: {
      type: String,
      default: 'Gallery'
    },
    post: {
      type: Object,
      default: () => {}
    }
  },

  data: () => ({
    btnClasses: 'absolute bottom-0 w-10 h-10 z-10 flex-c fill-white transition-colors duration-500 ease-in-out focus:bg-black focus:fill-white lg:hover:bg-white lg:hover:fill-accent-1 focus:outline-none',
    btnIconClasses: 'w-4 h-4',
    currentIndex: 0,
    rotate: false,
    rotating: false,
    interval: null,
    stop: 'Stop automatic slide show',
    start: 'Start automatic slideshow',
    touchstartX: 0,
    touchendX: 0,
    isTouch: false,
    lazyLoad: [0]
  }),

  computed: {
    gallery() { return this.post.gallery || [] },
    swipeZone() {
      return this.$refs.RvCarouselItems
    }
  },

  watch: {
    rotate: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.interval = setInterval(() => {
            this.currentIndex = this.currentIndex + 1 < this.gallery.length ? this.currentIndex + 1 : 0
            this.handleLazyLoad()
          }, 3000)
        } else {
          clearInterval(this.interval)
        }
      }
    }
  },

  mounted() {
    this.addListeners()
  },

  beforeDestroy() {
    window.removeEventListener('keyup', this.handleArrowKeys)
    window.removeEventListener('touchstart', this.handleTouch)
  },

  methods: {
    handlePrevNext(direction) {
      const length = this.gallery.length
      const index = this.currentIndex

      if (this.isTouch) {
        this.rotate = false
        this.rotating = false
      }

      switch (direction) {
      case 'next': this.currentIndex = (index + 1) % length
        break
      case 'prev': this.currentIndex = (index === 0 && length - 1) || index - 1
        break
      default: this.currentIndex = index
      }

      this.handleLazyLoad()
    },
    handleArrowKeys() {
      switch (event.keyCode) {
        case 39:
          this.handlePrevNext('next')
          break
        case 37:
          this.handlePrevNext('prev')
          break
        default:
          return
      }
    },
    handleRotation(value) {
      if (!this.isTouch) {
        this.rotate = value
      }
    },
    addListeners() {
      window.addEventListener('keyup', this.handleArrowKeys)
      window.addEventListener('touchstart', this.handleTouch)

      this.swipeZone.addEventListener('touchstart', (event) => {
        this.touchstartX = event.changedTouches[0].screenX
      }, false)

      this.swipeZone.addEventListener('touchend', (event) => {
        this.touchendX = event.changedTouches[0].screenX
        this.handleSwipe()
      }, false)
    },
    handleTouch() {
      window.removeEventListener('touchstart', this.handleTouch)
      this.$refs.RvCarousel.classList.remove('no-touch')
      this.isTouch = true
    },
    handleSwipe() {
      if (this.isTouch) {
        this.rotate = false
        this.rotating = false
      }

      if (this.touchendX < this.touchstartX) {
        this.handlePrevNext('prev')
      }

      if (this.touchendX > this.touchstartX) {
        this.handlePrevNext('next')
      }
    },
    handleLazyLoad() {
      if (!this.lazyLoad.includes(this.currentIndex)) {
        this.lazyLoad.push(this.currentIndex)
      }
    }
  }
}
</script>
